import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

interface OnboardingScreenProps {
  header: ReactNode;
  footer: ReactNode;
  children: ReactNode;
  className?: string;
  slot?: string;
}

/** Viewport-locked onboarding chrome with 24px gaps around the middle slot. */
export function OnboardingScreen({
  header,
  footer,
  children,
  className,
  slot,
}: OnboardingScreenProps) {
  return (
    <div
      data-slot={slot}
      className={cn(
        'mx-auto flex h-dvh w-full max-w-[500px] flex-col overflow-hidden bg-mf-neutral-50 py-4 font-body',
        className,
      )}
    >
      <div className="w-full shrink-0 px-4">{header}</div>
      <div className="flex min-h-0 w-full flex-1 flex-col py-6">{children}</div>
      <div className="w-full shrink-0 px-4">{footer}</div>
    </div>
  );
}

interface ScaleToFitProps {
  naturalHeight: number;
  children: ReactNode;
  className?: string;
  /** Keep visual width at 100% so side-bleed (e.g. the logo ring) stays on screen. */
  lockWidth?: boolean;
}

/**
 * Uniformly scales children down when the slot is shorter than `naturalHeight`.
 * Never scales above 1 — extra space stays as larger gaps (parent padding is the floor).
 */
export function ScaleToFit({
  naturalHeight,
  children,
  className,
  lockWidth = false,
}: ScaleToFitProps) {
  const slotRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  useLayoutEffect(() => {
    const slot = slotRef.current;
    if (!slot) return;

    const update = () => {
      const available = slot.clientHeight;
      if (available <= 0) return;
      setScale(Math.min(1, available / naturalHeight));
    };

    update();
    const observer = new ResizeObserver(update);
    observer.observe(slot);
    return () => observer.disconnect();
  }, [naturalHeight]);

  return (
    <div ref={slotRef} className={cn('relative min-h-0 w-full flex-1 overflow-hidden', className)}>
      <div
        className="absolute left-1/2 top-1/2"
        style={{
          height: naturalHeight,
          width: lockWidth ? `${100 / scale}%` : '100%',
          transform: `translate(-50%, -50%) scale(${scale})`,
        }}
      >
        {children}
      </div>
    </div>
  );
}
